// The authenticated fetch wrapper, shared.
//
// Each app calls createApiClient() with its own base URL, so one implementation of
// "attach the bearer token, decode the error, shout on 401" serves all of them.
//
// ⚠️ THIS IS THE FILE THE WHOLE EXTRACTION IS FOR. Duplicating request/response
// handling is how two apps end up disagreeing about what a 401 means, or one of them
// quietly not sending the token. The dashboard's own copy predates this and is not yet
// migrated — see shared/auth/README.md.

import { getStoredToken } from './tokenStore.js';

export function createApiClient({ baseUrl = '/api' } = {}) {
  const base = String(baseUrl).replace(/\/+$/, '');

  async function request(method, path, { body, headers, signal } = {}) {
    const token = getStoredToken();
    const res = await fetch(`${base}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal
    });

    if (res.status === 204) return null;

    const type = res.headers.get('content-type') || '';
    const data = type.includes('application/json')
      ? await res.json().catch(() => null)
      : await res.text().catch(() => null);

    if (!res.ok) {
      // The provider listens for this and clears the session. Dispatched rather than
      // handled here so the transport never has to know about React.
      if (res.status === 401) {
        try { window.dispatchEvent(new CustomEvent('auth:unauthorized')); } catch { /* no window */ }
      }
      const err = new Error((data && (data.message || data.error)) || `HTTP ${res.status}`);
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  }

  const api = {
    get: (p, o) => request('GET', p, o),
    post: (p, body, o) => request('POST', p, { ...o, body }),
    put: (p, body, o) => request('PUT', p, { ...o, body }),
    patch: (p, body, o) => request('PATCH', p, { ...o, body }),
    del: (p, o) => request('DELETE', p, o)
  };

  return {
    api,
    auth: {
      // This app's OWN API. It used to post to the voice platform's core, which had to
      // be told which app was asking via an enum it kept centrally — and when the
      // client forgot to say, core mailed the user a link to a different product and
      // the login screen still said "check your email". There is no such ambiguity
      // here: one backend, one frontend, one link target, set by FRONTEND_URL.
      sendMagicLink: (email) => api.post('/auth/request', { email }),
      verifyMagicLink: (token) => api.post('/auth/verify', { token })
    }
  };
}
